<template>
  <AudioWrapper
    :ref="refName"
    :src="link.src"
    @ended.native="ended"
    @error="errorHandler"
  />
</template>

<script>
import { mapGetters } from 'vuex';

import Utils from '@/services/utils/Utils';
import AppConstants from '@/services/utils/AppConstantsUtil';
import AnnotationAudioEvents from '@/enums/AnnotationAudioEvents';
import CustomErrorEnum from '@/enums/CustomErrorEnum';

import AudioWrapper from '@/components/views/Audio/AudioWrapper';
import LinkFactory from '@/classes/factories/LinkFactory';
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('AnnotationAudio.vue');

export default {
  components: {
    AudioWrapper
  },
  props: { bookId: String },
  data() {
    const link = LinkFactory.createEmptyLink();
    return {
      refName: 'annAudio',
      link,
      src: '',
      timeToStop: 0,
      stopAudioTimeout: null,
      endSubscription: null,
      raf: null
    };
  },
  computed: {
    ...mapGetters({
      playbackRate: 'ReadingSettingsStore/getPlaybackRate',
      audioLinks: 'PublicationStore/getAudioLinks'
    }),
    currentAudioElement() {
      return this.$refs[this.refName];
    },
    getPlayingAnnId() {
      return this.$store.getters['AnnotationsStore/getPlayingAnnId']();
    }
  },
  mounted() {
    this.$_subscribe();
  },
  destroyed() {
    if (this.endSubscription) {
      this.endSubscription();
    }
  },
  methods: {
    $_subscribe() {
      this.endSubscription = this.$store.subscribeAction(
        ({ type, payload }) => {
          switch (type) {
            case 'AnnotationsStore/playAnnotation':
            case 'PlaybackStore/playHighlight':
              this.playAudio(payload.locator);
              break;
            case 'AnnotationsStore/stopPlayingAnnotation':
            case 'PlaybackStore/stopPlayingHighlight':
              this.pause();
              break;
            case 'AnnotationsStore/playTtsAnnotation':
            case 'PlaybackStore/playTts':
              this.playTts(payload);
              break;
            case 'PlaybackStore/playFromLocator':
            case 'PlaybackStore/playParagraph':
              this.pause();
              break;
            default:
              break;
          }
        }
      );
    },
    async playTts({ text, lang, locator }) {
      try {
        if (this.getPlayingAnnId) {
          await this.pause();
        }
        const { data } = await this.$store.dispatch(
          'AnnotationsStore/sendRequestToCreateTts',
          {
            text,
            lang
          }
        );
        if (data instanceof Blob && data.type === 'audio/mpeg') {
          const src = window.URL.createObjectURL(data);
          this.link.setSrc(src);
          this.timeToStop = 0;
          this.$_play(0);
        } else {
          const errorResponse = JSON.parse(await data.text());
          if (errorResponse.limitExceeded) {
            this.$store.dispatch('ManagePopupStore/openToaster', {
              text: this.$t('Annotations.Audio.Polly.limit.error')
            });
            this.$_stopPlay();
          } else {
            const err = new Error('get incorrect server response');
            err.type = CustomErrorEnum.EMPTY_DATA;
            this.errorHandler(err);
          }

          const publicationId = this.$store.getters[
            'OpenParameterStore/getPublicationId'
          ];
          const isAudioBook = this.$store.getters[
            'PublicationStore/isAudioPublication'
          ](publicationId);

          if (isAudioBook) {
            this.playAudio(locator);
            return;
          }
        }
      } catch (err) {
        this.errorHandler(err);
      }
    },
    async playAudio(locator) {
      try {
        if (this.getPlayingAnnId) {
          await this.pause();
        }
        const paraId = locator.startLocator.prefixedParagraphId;
        const src = this.audioLinks()[paraId];
        this.link.setSrc(src);
        await this.preloadAlignmentBeforePlay(paraId);
        const startTime = this.$store.getters[
          'PublicationStore/getTimeByLocator'
        ](this.bookId, locator.startLocator);
        this.timeToStop = this.$store.getters[
          'PublicationStore/getEndTimeByLocator'
        ](this.bookId, locator.endLocator);
        this.$_play(startTime);
      } catch (err) {
        this.errorHandler(err);
      }
    },
    async preloadAlignmentBeforePlay(paraId) {
      this.pause();
      try {
        await this.$store.dispatch('PlaybackStore/preloadAlignment', {
          paraId,
          bookId: this.bookId
        });
      } catch (err) {
        logger.warn(`Error occured: ${err}`);
      }
    },
    async $_play(time) {
      await Utils.waitDuration(this.currentAudioElement);
      await this.currentAudioElement.setCurrentTime(
        time / AppConstants.MS_IN_SEC
      );
      this.currentAudioElement.playbackRate = this.playbackRate;
      this.$_startRAFLoop();
      this.$_cleanStopAudioTimeout();
      this.currentAudioElement.play();
      this.$store.commit('PlaybackStore/setIsHighlightPlaying', true);
      this.$_initStopAudioTimeout(time);
    },

    ended() {
      this.$_stopPlay();
    },

    $_stopPlay() {
      this.$store.dispatch('AnnotationsStore/stopPlayingAnnotation');
      this.pause();
    },

    $_initStopAudioTimeout(time) {
      const minLength = 150;
      const audioPlayTime = Math.max(this.timeToStop - time, minLength);
      if (this.timeToStop) {
        this._runStopAudioTimeout(audioPlayTime, this.timeToStop);
      }
    },

    _runStopAudioTimeout(audioDuration, endTime, count = 0) {
      count += 1;
      const accuracy = 50;
      const deltaCorrection = 50;
      this.stopAudioTimeout = setTimeout(() => {
        const currentTime =
          this.currentAudioElement.getCurrentTime() * AppConstants.MS_IN_SEC;
        const deltaOnEnd = endTime - currentTime - deltaCorrection;

        const tooManyAttempt = count > 5;
        if (deltaOnEnd > accuracy && !tooManyAttempt) {
          this._runStopAudioTimeout(deltaOnEnd, endTime, count);
          return;
        } else if (tooManyAttempt) {
          logger.error(
            `too many attempt for stop annotation audio bookId:${this.bookId}, currentParaId:${this.currentParaId}, accuracy: ${accuracy}, deltaOnEnd:${deltaOnEnd}`
          );
        }

        this.$_stopPlay();
      }, audioDuration);
    },

    $_cleanStopAudioTimeout() {
      if (this.stopAudioTimeout) {
        clearTimeout(this.stopAudioTimeout);
        this.stopAudioTimeout = null;
      }
    },

    $_startRAFLoop() {
      this.$_stopRAFLoop();
      this.raf = window.requestAnimationFrame(() => {
        this.$_startRAFLoop();
      });
    },
    $_stopRAFLoop() {
      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
        this.raf = null;
      }
    },
    errorHandler(error) {
      logger.error(`Error occured in AnnotationAudio: ${error}`);
      this.$_stopPlay();
      this.$emit('annotationAudioEvent', {
        type: AnnotationAudioEvents.ERROR,
        data: { error }
      });
    },
    pause() {
      if (!this.currentAudioElement) {
        return;
      }
      this.$store.commit('PlaybackStore/setIsHighlightPlaying', false);
      this.currentAudioElement.pause();
      this.$_cleanStopAudioTimeout();
      this.$_stopRAFLoop();
    }
  }
};
</script>

<style></style>
